import React, { useEffect, useRef, useState } from 'react';
import {
    ExpressCheckoutElement,
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import type {
    StripeExpressCheckoutElementClickEvent,
    StripeExpressCheckoutElementConfirmEvent,
    StripeExpressCheckoutElementOptions,
    StripePaymentElementChangeEvent,
    StripePaymentElementOptions
} from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useUserState } from "../../providers/user-state.provider.ts";
import { createSubscription } from '../../services/payment.service';
import { createAnalyticsClientEventId } from '../../services/analytics.service';
import { getPaymentErrorDetails } from '../../utils/payment-error';
import type { PaymentErrorDetails } from '../../utils/payment-error';
import { createCheckoutAttemptId } from '../../utils/checkout-attempt';
import { useAuthModal } from '../../providers/auth-modal.provider';
import { Routes } from '../../types/constants';
import type { SubscriptionPlan } from '../../types/payment.types';
import type { UserInfo } from '../../types/user.types';

const expressCheckoutOptions: StripeExpressCheckoutElementOptions = {
    buttonType: {
        applePay: 'subscribe',
        googlePay: 'subscribe'
    },
    paymentMethodOrder: ['applePay', 'googlePay'],
    paymentMethods: {
        applePay: 'always',
        googlePay: 'always',
        link: 'never',
        paypal: 'never',
        amazonPay: 'never'
    }
};

const paymentElementOptions: StripePaymentElementOptions = {
    wallets: {
        applePay: 'never',
        googlePay: 'never'
    },
    fields: {
        billingDetails: {
            email: 'never'
        }
    }
};

type ApplePayInterval = {
    unit: 'year' | 'month' | 'day';
    count: number;
};

const getApplePayInterval = (interval: string, count: number): ApplePayInterval | null => {
    switch (interval) {
        case 'year':
        case 'month':
        case 'day':
            return { unit: interval, count };
        case 'week':
            return { unit: 'day', count: count * 7 };
        default:
            return null;
    }
};

type CheckoutFormProps = {
    offering: SubscriptionPlan;
    checkoutAttemptId?: string;
    entryPoint?: string;
    returnTo?: string;
    submitLabel?: string;
    amountLabel?: string;
    recurrenceLabel?: string;
    showCancelButton?: boolean;
    expressCheckoutEnabled?: boolean;
    validateBeforePayment?: () => boolean;
    ensureAuthenticated?: (checkoutAttemptId: string) => Promise<UserInfo & { checkoutAttemptId?: string }>;
    onCheckoutAttemptChanged?: (checkoutAttemptId: string) => void;
    onCheckoutStarted?: () => void;
    onSubmittingChange?: (isSubmitting: boolean) => void;
};

type SubscriptionPaymentMethodType = 'apple_pay' | 'google_pay' | 'card';

type ExpressCheckoutErrorBoundaryProps = {
    children: React.ReactNode;
    onError: (error: Error) => void;
};

type ExpressCheckoutErrorBoundaryState = {
    failed: boolean;
};

class ExpressCheckoutErrorBoundary extends React.Component<
    ExpressCheckoutErrorBoundaryProps,
    ExpressCheckoutErrorBoundaryState
> {
    state: ExpressCheckoutErrorBoundaryState = { failed: false };

    static getDerivedStateFromError(): ExpressCheckoutErrorBoundaryState {
        return { failed: true };
    }

    componentDidCatch(error: Error) {
        // Stripe can throw during element mounting before onLoadError is available.
        // Keep the card Payment Element usable when the optional wallet element fails.
        this.props.onError(error);
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

const getPaymentMethodType = (
    event?: StripeExpressCheckoutElementConfirmEvent | StripeExpressCheckoutElementClickEvent
): SubscriptionPaymentMethodType => {
    if (event?.expressPaymentType === 'apple_pay') return 'apple_pay';
    if (event?.expressPaymentType === 'google_pay') return 'google_pay';
    return 'card';
};

/**
 * Stripe Elements checkout form for subscriptions.
 */
const CheckoutForm = ({
    offering,
    checkoutAttemptId: suppliedCheckoutAttemptId,
    entryPoint = 'direct',
    returnTo,
    submitLabel = 'Subscribe now',
    amountLabel,
    recurrenceLabel,
    showCancelButton = true,
    expressCheckoutEnabled = true,
    validateBeforePayment,
    ensureAuthenticated,
    onCheckoutAttemptChanged,
    onCheckoutStarted,
    onSubmittingChange
}: CheckoutFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [paymentError, setPaymentError] = useState<PaymentErrorDetails | null>(null);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isPaymentDetailsComplete, setPaymentDetailsComplete] = useState(false);
    const [isExpressCheckoutReady, setExpressCheckoutReady] = useState(false);
    const [isExpressCheckoutAvailable, setExpressCheckoutAvailable] = useState(false);
    const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
    const [checkoutAttemptId, setCheckoutAttemptId] = useState(
        suppliedCheckoutAttemptId || createCheckoutAttemptId
    );
    const submissionInProgress = useRef(false);
    const navigate = useNavigate();
    const { openModal } = useAuthModal();
    const user = useUserState();

    useEffect(() => {
        if (suppliedCheckoutAttemptId && suppliedCheckoutAttemptId !== checkoutAttemptId) {
            setCheckoutAttemptId(suppliedCheckoutAttemptId);
        }
    }, [checkoutAttemptId, suppliedCheckoutAttemptId]);

    useEffect(() => {
        if (!expressCheckoutEnabled) {
            setExpressCheckoutReady(false);
            setExpressCheckoutAvailable(false);
        }
    }, [expressCheckoutEnabled]);

    const replaceAttempt = (nextCheckoutAttemptId: string) => {
        setCheckoutAttemptId(nextCheckoutAttemptId);
        onCheckoutAttemptChanged?.(nextCheckoutAttemptId);
    };

    /**
     * Submit payment details and confirm the intent with Stripe.
     */
    const confirmCheckout = async (expressEvent?: StripeExpressCheckoutElementConfirmEvent) => {
        if (!expressEvent && !isPaymentDetailsComplete) {
            onSubmittingChange?.(false);
            return;
        }
        if (!stripe || !elements || submissionInProgress.current) {
            if (expressEvent && submissionInProgress.current) {
                expressEvent.paymentFailed({ reason: 'fail' });
            }
            if (!submissionInProgress.current) {
                onSubmittingChange?.(false);
            }
            return;
        }

        if (validateBeforePayment && !validateBeforePayment()) {
            expressEvent?.paymentFailed({ reason: 'fail' });
            onSubmittingChange?.(false);
            return;
        }

        submissionInProgress.current = true;
        onSubmittingChange?.(true);
        setLoading(true);
        setPaymentError(null);
        setCheckoutStatus(null);

        const paymentMethodType = getPaymentMethodType(expressEvent);
        let effectiveCheckoutAttemptId = checkoutAttemptId;
        const currentUserInfo = user.getUserInfo();

        try {
            let userInfo = currentUserInfo;
            if (ensureAuthenticated) {
                try {
                    userInfo = await ensureAuthenticated(effectiveCheckoutAttemptId);
                } catch (error: unknown) {
                    // Account errors are rendered beside the account fields by the caller. Only
                    // checkout-attempt expiry belongs to payment recovery and needs a new attempt.
                    if (getPaymentErrorDetails(error).action === 'restart_checkout') {
                        throw error;
                    }
                    expressEvent?.paymentFailed({ reason: 'fail' });
                    return;
                }
            }
            if (!userInfo) throw new Error('User not logged in.');
            const authenticatedCheckoutAttemptId = 'checkoutAttemptId' in userInfo
                && typeof userInfo.checkoutAttemptId === 'string'
                ? userInfo.checkoutAttemptId
                : undefined;
            if (authenticatedCheckoutAttemptId && authenticatedCheckoutAttemptId !== effectiveCheckoutAttemptId) {
                effectiveCheckoutAttemptId = authenticatedCheckoutAttemptId;
                replaceAttempt(effectiveCheckoutAttemptId);
            }

            const result = await elements.submit();
            if (result.error) throw result.error;

            // Backend returns clientSecret and intent type for Stripe confirmation.
            const checkoutEventId = createAnalyticsClientEventId();
            let checkoutResponse: Awaited<ReturnType<typeof createSubscription>>;
            try {
                onCheckoutStarted?.();
                checkoutResponse = await createSubscription(
                    offering.offeringId,
                    effectiveCheckoutAttemptId,
                    userInfo.token,
                    checkoutEventId,
                    { entryPoint, paymentMethodType }
                );
            } catch (error: unknown) {
                const recovery = getPaymentErrorDetails(error);
                if (recovery.action !== 'restart_checkout') {
                    throw error;
                }

                // The server has retired a terminal Stripe intent. Retry once with a new
                // idempotency reservation while the existing deferred Element keeps its input.
                const restartedCheckoutAttemptId = createCheckoutAttemptId();
                effectiveCheckoutAttemptId = restartedCheckoutAttemptId;
                replaceAttempt(restartedCheckoutAttemptId);
                onCheckoutStarted?.();
                checkoutResponse = await createSubscription(
                    offering.offeringId,
                    restartedCheckoutAttemptId,
                    userInfo.token,
                    checkoutEventId,
                    { entryPoint, paymentMethodType }
                );
            }
            const { clientSecret, type } = checkoutResponse;
            if (checkoutResponse.checkoutAttemptId
                && checkoutResponse.checkoutAttemptId !== effectiveCheckoutAttemptId) {
                effectiveCheckoutAttemptId = checkoutResponse.checkoutAttemptId;
                replaceAttempt(effectiveCheckoutAttemptId);
            }
            if (!clientSecret || (type !== 'payment' && type !== 'setup')) {
                throw new Error('The payment service returned an incomplete checkout response. Please try again.');
            }

            // Confirm the subscription
            const successPath = returnTo
                ? `/payment_completed?returnTo=${encodeURIComponent(returnTo)}`
                : '/payment_completed';
            const confirmationOptions = {
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}${successPath}`,
                    payment_method_data: {
                        billing_details: {
                            email: userInfo.email
                        }
                    }
                },
                redirect: 'if_required' as const
            };
            const { error } = type === 'setup'
                ? await stripe.confirmSetup(confirmationOptions)
                : await stripe.confirmPayment(confirmationOptions);

            if (error) throw error;
            navigate(successPath);
        } catch (error: unknown) {
            expressEvent?.paymentFailed({ reason: 'fail' });
            const nextError = getPaymentErrorDetails(error);
            if (nextError.action === 'restart_checkout') {
                // Start a new server-side attempt without remounting Stripe Elements or losing its input state.
                replaceAttempt(createCheckoutAttemptId());
            }
            setPaymentError(nextError);
        } finally {
            submissionInProgress.current = false;
            onSubmittingChange?.(false);
            setLoading(false);
        }
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void confirmCheckout();
    };

    const handleExpressConfirm = (event: StripeExpressCheckoutElementConfirmEvent) => {
        void confirmCheckout(event);
    };

    const handleExpressClick = (event: StripeExpressCheckoutElementClickEvent) => {
        if (validateBeforePayment && !validateBeforePayment()) {
            event.resolve();
            return;
        }
        onSubmittingChange?.(true);
        const interval = getApplePayInterval(
            offering.recurringInterval,
            offering.recurringIntervalCount
        );
        if (event.expressPaymentType !== 'apple_pay' || !interval) {
            event.resolve();
            return;
        }

        event.resolve({
            applePay: {
                recurringPaymentRequest: {
                    paymentDescription: `Combat TV ${offering.duration} subscription`,
                    managementURL: `${window.location.origin}${Routes.PROFILE_BILLING}`,
                    regularBilling: {
                        label: `Combat TV ${offering.duration}`,
                        amount: offering.unitAmount,
                        recurringPaymentIntervalUnit: interval.unit,
                        recurringPaymentIntervalCount: interval.count
                    },
                    billingAgreement: 'You authorize Combat TV to charge this subscription until it is cancelled.'
                }
            }
        });
    };

    const handleExpressCheckoutFailure = (error?: Error) => {
        if (error) {
            // Do not report the error object or message because Stripe errors can contain payment context.
            console.error('Express Checkout Element failed to mount.', {
                errorName: error.name || 'Error'
            });
        }
        onSubmittingChange?.(false);
        setExpressCheckoutReady(true);
        setExpressCheckoutAvailable(false);
        setCheckoutStatus('Express payment is unavailable. Pay by card below.');
    };

    return (
        <form
            id="payment-form"
            aria-label="Subscription payment"
            aria-busy={isLoading}
            onSubmit={handleSubmit}
        >
            {expressCheckoutEnabled && !isExpressCheckoutReady ? (
                <span className="sr-only" role="status">
                    Checking express payment options.
                </span>
            ) : null}
            {expressCheckoutEnabled ? (
                <div
                    aria-hidden={!isExpressCheckoutAvailable}
                    className={isExpressCheckoutAvailable ? 'mb-4' : ''}
                    data-testid="express-checkout-container"
                    style={{
                        visibility: isExpressCheckoutAvailable ? 'visible' : 'hidden',
                        height: isExpressCheckoutAvailable ? 'auto' : 0,
                        overflow: 'hidden'
                    }}
                >
                    <ExpressCheckoutErrorBoundary onError={handleExpressCheckoutFailure}>
                        <ExpressCheckoutElement
                            options={expressCheckoutOptions}
                            onReady={({ availablePaymentMethods }) => {
                                setExpressCheckoutReady(true);
                                setExpressCheckoutAvailable(!!availablePaymentMethods && (
                                    availablePaymentMethods.applePay || availablePaymentMethods.googlePay
                                ));
                            }}
                            onClick={handleExpressClick}
                            onConfirm={handleExpressConfirm}
                            onCancel={() => {
                                if (!submissionInProgress.current) {
                                    onSubmittingChange?.(false);
                                    setPaymentError(null);
                                    setCheckoutStatus(
                                        'Express payment was cancelled. You can try again or pay by card below.'
                                    );
                                }
                            }}
                            onLoadError={() => handleExpressCheckoutFailure()}
                        />
                    </ExpressCheckoutErrorBoundary>
                </div>
            ) : (
                <p className="mb-3 text-xs leading-5 text-gray-600" role="status">
                    Wallets appear after account details.
                </p>
            )}
            {isExpressCheckoutAvailable ? (
                <div className="mb-4 flex items-center gap-3" aria-hidden="true">
                    <span className="h-px flex-1 bg-gray-300" />
                    <span className="text-sm text-gray-500">Or pay by card</span>
                    <span className="h-px flex-1 bg-gray-300" />
                </div>
            ) : null}
            {checkoutStatus ? (
                <div
                    role="status"
                    className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"
                >
                    {checkoutStatus}
                </div>
            ) : null}
            {paymentError && (
                <div
                    role="alert"
                    className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700"
                >
                    <p>{paymentError.message}</p>
                    <p className="mt-1 text-sm">
                        The secure payment form remains available below. Correct anything highlighted and try again.
                    </p>
                    {paymentError.action === 'sign_in' ? (
                        <button
                            type="button"
                            className="mt-2 font-semibold underline"
                            onClick={() => openModal('sign_in')}
                        >
                            Sign in again
                        </button>
                    ) : null}
                    {paymentError.action === 'manage_subscription' ? (
                        <button
                            type="button"
                            className="mt-2 font-semibold underline"
                            onClick={() => navigate(Routes.PROFILE_BILLING)}
                        >
                            Manage subscription
                        </button>
                    ) : null}
                    {paymentError.action === 'choose_plan' ? (
                        <button
                            type="button"
                            className="mt-2 font-semibold underline"
                            onClick={() => navigate('/pricing')}
                        >
                            Choose another plan
                        </button>
                    ) : null}
                </div>
            )}
            <div className="mb-4">
                <PaymentElement
                    id="payment-element"
                    className="block w-full"
                    options={paymentElementOptions}
                    onChange={(event: StripePaymentElementChangeEvent) => {
                        setPaymentDetailsComplete(event.complete);
                        setCheckoutStatus(null);
                    }}
                />
            </div>
            {amountLabel ? (
                <div className="mb-3 flex items-baseline justify-between gap-3 text-gray-800" aria-label="Subscription total">
                    <span className="text-sm">Due today</span>
                    <span className="text-right">
                        <strong className="text-lg">{amountLabel}</strong>
                        {recurrenceLabel ? <small className="block text-xs text-gray-500">{recurrenceLabel}</small> : null}
                    </span>
                </div>
            ) : null}
            <button
                type="submit"
                disabled={isLoading || !stripe || !elements || !isPaymentDetailsComplete}
                className={`min-h-12 w-full rounded-lg bg-blue-700 text-sm text-white font-semibold py-3 px-4 shadow-md hover:bg-blue-800 transition ${
                    isLoading || !stripe || !elements || !isPaymentDetailsComplete
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                }`}
            >
                {isLoading ? (
                    <>
                        <span
                            aria-hidden="true"
                            className="inline-block spinner border-t-4 border-white w-5 h-5 rounded-full animate-spin"
                        />
                        <span className="sr-only">Processing subscription</span>
                    </>
                ) : (
                    paymentError ? "Try payment again" : submitLabel
                )}
            </button>
            {showCancelButton ? (
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="min-h-11 w-full rounded-xl bg-gray-200 text-black font-semibold py-3 px-4 hover:bg-gray-300 transition mt-3"
                >
                    Cancel
                </button>
            ) : null}
        </form>
    );
};

export default CheckoutForm;
